// src/hooks/useWording.js
/**
 * React binding for the product-presentation wording resolver.
 *
 * Reads the authenticated user's resolved product-presentation profile from the
 * auth context and returns the localized subject/site/staff labels the UI uses
 * in place of ambiguous doubled terms ("Patients / Subjects").
 *
 * Prefer the org-specific `productPresentationProfile`, falling back to the
 * region-derived `deploymentProductPresentationProfile` (mirroring how the
 * dashboard resolves feature access), and finally to safe default terms.
 *
 * @module hooks/useWording
 */

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";
import { buildWording, resolveWordingTerms } from "../utils/wording";

/**
 * @returns {{
 *   terms: { subjectTerm: string, siteTerm: string, staffTerm: string },
 *   subject: string, subjects: string,
 *   site: string, sites: string,
 *   staff: string, staffs: string,
 *   subjectLower: string, subjectsLower: string,
 *   siteLower: string, sitesLower: string,
 *   staffLower: string, staffsLower: string,
 *   vars: Record<string, string>,
 *   tw: (key: string, defaultValueOrOptions?: string | object, options?: object) => string
 * }}
 */
export function useWording() {
  const { t } = useTranslation();
  const { accountContext } = useAuth();

  const profile =
    accountContext?.productPresentationProfile ||
    accountContext?.deploymentProductPresentationProfile ||
    null;

  const wording = useMemo(
    () => buildWording(resolveWordingTerms(profile), t),
    // `t` identity changes on language load; `profile` on context change.
    [profile, t],
  );

  // A drop-in i18next translator that auto-injects the wording variables, so
  // interpolated strings ("Create {{siteLower}}") resolve without every call
  // site having to remember to pass them. Mirrors i18next's two call shapes:
  // `t(key, defaultValue, options)` and `t(key, options)`, normalising both to
  // the `t(key, { defaultValue, ...options })` object form.
  const tw = useCallback(
    (key, defOrOpts, maybeOpts) => {
      if (defOrOpts !== null && typeof defOrOpts === "object") {
        return t(key, { ...wording.vars, ...defOrOpts });
      }
      return t(key, {
        defaultValue: defOrOpts,
        ...wording.vars,
        ...(maybeOpts || {}),
      });
    },
    [t, wording.vars],
  );

  return useMemo(() => ({ ...wording, tw }), [wording, tw]);
}

export default useWording;
